import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertiesAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { Building2, Plus, MapPin, Edit2, Trash2, Search, RefreshCw } from 'lucide-react';
import { fmt, errMsg } from '../utils/helpers';
import { usePermissions } from '../context/PermissionsContext';

const EMPTY = { name:'', address:'', city:'', state:'MD', zip:'', type:'Multi-Family', totalUnits:'', manager:'', yearBuilt:'', amenities:'' };
const TYPES     = ['Multi-Family','High-Rise','Townhome','Single Family','Commercial'];
const TYPE_KEYS = { 'Multi-Family':'multiFamily','High-Rise':'highRise','Townhome':'townhome','Single Family':'singleFamily','Commercial':'commercial' };
const COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#e11d48'];

export default function Properties() {
  const navigate  = useNavigate();
  const { t }    = useTranslation();
  const { can }  = usePermissions();
  const canCreate = can('properties', 'create');
  const canEdit   = can('properties', 'edit');
  const canDelete = can('properties', 'delete');
  const [props, setProps]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [modal, setModal]     = useState(false);
  const [edit, setEdit]       = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    propertiesAPI.getAll().then(r => setProps(r.data.data)).catch(() => toast.error(t('error'))).finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filtered = props.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.city.toLowerCase().includes(search.toLowerCase()));

  const openAdd  = () => { setForm(EMPTY); setEdit(null); setModal(true); };
  const openEdit = p  => { setForm({ ...p, amenities: (p.amenities||[]).join(', ') }); setEdit(p._id); setModal(true); };

  const handleSave = async () => {
    if (!form.name || !form.address) return toast.warning(t('propertyName') + ' ' + t('streetAddress') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = { ...form, totalUnits: Number(form.totalUnits)||0, yearBuilt: Number(form.yearBuilt)||undefined, amenities: form.amenities ? form.amenities.split(',').map(s=>s.trim()).filter(Boolean) : [] };
      if (edit) { await propertiesAPI.update(edit, data); toast.success(t('propertyUpdated')); }
      else       { await propertiesAPI.create(data);       toast.success(t('propertyCreated')); }
      setModal(false); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('deactivateProperty'))) return;
    try { await propertiesAPI.delete(id); toast.success(t('propertyDeactivated')); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('properties')}</h1>
          <p className="page-subtitle">{props.length} {t('propertiesInPortfolio')}</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16} /></button>
          {canCreate && <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/>{t('addProperty')}</button>}
        </div>
      </div>

      <div style={{ marginBottom:22 }}>
        <div className="search-bar" style={{ width:'100%', maxWidth:360 }}>
          <Search size={15} color="var(--ink-20)"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('searchProperties')}/>
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner"/></div>
      ) : (
        <div className="grid grid-3">
          {filtered.map((p, idx) => {
            const color = COLORS[idx % COLORS.length];
            const rate  = p.unitCount ? Math.round((p.occupiedCount / p.unitCount) * 100) : 0;
            return (
              <div key={p._id} className="card" style={{ transition:'transform 180ms,box-shadow 180ms' }}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='var(--shadow-md)'}}
                onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow=''}}>
                {/* Banner */}
                <div style={{ height:90, background:`linear-gradient(135deg,${color}18,${color}35)`, borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', cursor:'pointer' }}
                  onClick={()=>navigate(`/apartments?property=${p._id}`)}>
                  <Building2 size={38} color={color} style={{ opacity:.55 }}/>
                  <div style={{ position:'absolute', top:10, right:10, display:'flex', gap:6 }}>
                    {canEdit   && <button className="btn btn-secondary btn-sm btn-icon" onClick={e=>{e.stopPropagation();openEdit(p)}}><Edit2 size={13}/></button>}
                    {canDelete && <button className="btn btn-ghost btn-sm btn-icon" style={{ color:'var(--rose)' }} onClick={e=>{e.stopPropagation();handleDelete(p._id)}}><Trash2 size={13}/></button>}
                  </div>
                  <span className="badge badge-brand" style={{ position:'absolute', bottom:10, left:12 }}>{p.type}</span>
                </div>

                <div className="card-body" style={{ cursor:'pointer' }} onClick={()=>navigate(`/apartments?property=${p._id}`)}>
                  <h3 style={{ fontSize:16, fontWeight:700, marginBottom:5 }}>{p.name}</h3>
                  <div style={{ display:'flex', alignItems:'center', gap:5, color:'var(--ink-40)', fontSize:13, marginBottom:16 }}>
                    <MapPin size={12}/>{p.address}, {p.city}, {p.state}
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
                    {[[t('unitsLabel'), p.unitCount||0],[t('occupiedLabel'),p.occupiedCount||0],[t('vacantLabel'),p.vacantCount||0],[t('revenueLabel'),fmt.currency(p.monthlyRevenue||0)]].map(([k,v])=>(
                      <div key={k} style={{ background:'var(--surface-2)', borderRadius:8, padding:'8px 11px' }}>
                        <div style={{ fontSize:11, color:'var(--ink-40)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:2 }}>{k}</div>
                        <div style={{ fontSize:15, fontWeight:700 }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom:12 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--ink-40)', marginBottom:4 }}>
                      <span>{t('occupancy')}</span><span style={{ fontWeight:700, color }}>{rate}%</span>
                    </div>
                    <div className="progress-bar"><div className="progress-fill" style={{ width:rate+'%', background:color }}/></div>
                  </div>

                  {(p.amenities||[]).length > 0 && (
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      {p.amenities.slice(0,4).map(a=><span key={a} className="badge badge-neutral">{a}</span>)}
                    </div>
                  )}

                  <div style={{ marginTop:14, paddingTop:12, borderTop:'1px solid var(--border-subtle)', display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--ink-40)' }}>
                    <span>{t('manager')}: <strong style={{ color:'var(--ink-60)' }}>{p.manager||'—'}</strong></span>
                    {p.yearBuilt && <span>{t('built')} {p.yearBuilt}</span>}
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="empty-state" style={{ gridColumn:'1/-1' }}>
              <Building2 size={40}/><h3>{t('noPropertiesFound')}</h3>
            </div>
          )}
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title={edit?t('editProperty'):t('addPropertyTitle')}
        footer={<><button className="btn btn-secondary" onClick={()=>setModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?t('saving'):t('saveProperty')}</button></>}>
        <div className="form-grid form-grid-2" style={{ gap:16 }}>
          <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">{t('propertyName')} *</label><input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Riverside Commons"/></div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">{t('streetAddress')} *</label><input className="form-input" value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} placeholder="1400 Riverside Dr"/></div>
          <div className="form-group"><label className="form-label">{t('city')}</label><input className="form-input" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} placeholder="Germantown"/></div>
          <div className="form-group"><label className="form-label">{t('state')}</label><input className="form-input" value={form.state} onChange={e=>setForm(f=>({...f,state:e.target.value}))} placeholder="MD" maxLength={2}/></div>
          <div className="form-group"><label className="form-label">{t('zipCode')}</label><input className="form-input" value={form.zip} onChange={e=>setForm(f=>({...f,zip:e.target.value}))} placeholder="20874"/></div>
          <div className="form-group"><label className="form-label">{t('propertyType')}</label><select className="form-input form-select" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>{TYPES.map(tp=><option key={tp} value={tp}>{t(TYPE_KEYS[tp])}</option>)}</select></div>
          <div className="form-group"><label className="form-label">{t('totalUnits')}</label><input className="form-input" type="number" value={form.totalUnits} onChange={e=>setForm(f=>({...f,totalUnits:e.target.value}))} placeholder="24"/></div>
          <div className="form-group"><label className="form-label">{t('yearBuilt')}</label><input className="form-input" type="number" value={form.yearBuilt} onChange={e=>setForm(f=>({...f,yearBuilt:e.target.value}))} placeholder="2005"/></div>
          <div className="form-group"><label className="form-label">{t('propertyManager')}</label><input className="form-input" value={form.manager} onChange={e=>setForm(f=>({...f,manager:e.target.value}))} placeholder="Manager name"/></div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">{t('amenities')}</label><input className="form-input" value={form.amenities} onChange={e=>setForm(f=>({...f,amenities:e.target.value}))} placeholder="Pool, Gym, Parking, Laundry"/></div>
        </div>
      </Modal>
    </div>
  );
}
